import { Fragment, useEffect, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import {
  subscribeToUserChannel,
  unsubscribeFromUserChannel,
  listenToUserChannelEvent
} from 'libs/echo/echo.lib';
import userStore from 'stores/user.store';
import arsSummaryReportStore from 'stores/ars-summary-report.store';
import dataExportsStore from 'stores/data-exports.store';
import apolloClient from 'libs/apollo/apollo.lib';

const ARS_SUMMARY_REPORT_FIELDS = `
  id
  report_type
  screening_tests_config
  comparison_config
  status
  user {
    ... on Athlete {
      id
      user {
        id
        first_name
        last_name
        full_name
      }
    }
    ... on Client {
      id
      user {
        id
        first_name
        last_name
        full_name
      }
    }
  }
  createdBy {
    __typename
    ... on BarwisAdmin {
      id
      user {
        id
        first_name
        last_name
        full_name
      }
    }
    ... on BarwisCoach {
      id
      user {
        id
        first_name
        last_name
        full_name
      }
    }
    ... on ExternalAdmin {
      id
      user {
        id
        first_name
        last_name
        full_name
      }
    }
    ... on ExternalCoach {
      id
      user {
        id
        first_name
        last_name
        full_name
      }
    }
  }
`;

const DATA_EXPORTS_FIELDS = `
  id
  type
  config
  status
  createdBy {
    __typename
    ... on BarwisAdmin {
      id
      user {
        id
        first_name
        last_name
        full_name
      }
    }
    ... on BarwisCoach {
      id
      user {
        id
        first_name
        last_name
        full_name
      }
    }
    ... on ExternalAdmin {
      id
      user {
        id
        first_name
        last_name
        full_name
      }
    }
    ... on ExternalCoach {
      id
      user {
        id
        first_name
        last_name
        full_name
      }
    }
    ... on Athlete {
      id
      user {
        id
        first_name
        last_name
        full_name
      }
    }
    ... on Client {
      id
      user {
        id
        first_name
        last_name
        full_name
      }
    }
  }
`;

const Listener = () => {
  const isAuthenticated = userStore.getIsAuthenticated();
  const user = userStore.getUser();
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);

  // Update ref whenever pathname changes
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  const handleArsReportCompleted = useCallback(
    async (data) => {
      if (data) {
        const { status } = data; 
        if (status === 'COMPLETED') { 
          // Refresh data if currently on the ARS Summary Reports list page
          // Use ref to get current pathname without causing re-subscription
          if (pathnameRef.current === '/reports/ars-reports/ars-summary-reports') {
            try {
              await apolloClient.clearStore();
              arsSummaryReportStore.setFields(ARS_SUMMARY_REPORT_FIELDS);
              await arsSummaryReportStore.searchObjects();
            } catch (error) {
              console.error('Error refreshing ARS Summary Reports:', error);
            }
          }
        }
      }
    },
    [] // No dependencies - callback is stable
  );

  const handleDataExportCompleted = useCallback(
    async (data) => {
      if (data) {
        const { status } = data;
        if (status === 'COMPLETED') {
          // Refresh data if currently on the Data Exports list page
          if (pathnameRef.current === '/data-exports') {
            try {
              await apolloClient.clearStore();
              dataExportsStore.setFields(DATA_EXPORTS_FIELDS);
              await dataExportsStore.searchObjects();
            } catch (error) {
              console.error('Error refreshing Data Exports:', error);
            }
          }
        }
      }
    }, []
  );

  useEffect(() => {
    let unsubscribeArsReport = null;
    let unsubscribeDataExport = null;

    if (isAuthenticated && user?.id) {
      const userId = user.id;

      // Subscribe to user channel (shared across all components)
      subscribeToUserChannel(userId);

      // Listen to specific events
      unsubscribeArsReport = listenToUserChannelEvent(
        '.ars.summary.report.completed',
        handleArsReportCompleted
      );
      unsubscribeDataExport = listenToUserChannelEvent(
        '.data.export.completed',
        handleDataExportCompleted
      );
    } else {
      // Unsubscribe when user logs out
      unsubscribeFromUserChannel();
    }

    // Cleanup on unmount or when user changes
    return () => {
      if (unsubscribeArsReport) {
        unsubscribeArsReport();
        unsubscribeDataExport();
        unsubscribeFromUserChannel();
      }
    };
  }, [isAuthenticated, user?.id, handleArsReportCompleted]);

  return <Fragment></Fragment>;
};

export default observer(Listener);
